<main>
  <loading-overlay [isLoading]="isLoading > 0"></loading-overlay>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ ('Items_Inventory_Title' | translateWF: 'Item Inventory') || 'Item Inventory' }}</h1>
      </div>
    </div>

    <!-- View Mode Toggle and Actions -->
    <div class="row-between-center">
      <div class="row-start-center gap-3">
        <!-- View Mode Toggle: Lot Items | Inventory Summary -->
        <p-selectButton [formControl]="viewModeControl" [options]="viewModeOptions" (onChange)="onViewModeChange($event)" optionLabel="label" optionValue="value" />
      </div>

      <div class="row-sm-start-center">
        <p-button type="button" icon="pi pi-file-excel" (onClick)="exportToExcel()" class="p-button-outlined"></p-button>
      </div>
    </div>

    <!-- Inventory Filters -->
    <app-items-inventory-filters [class.hidden]="!showFilters" (isLoading)="onLoadingChange($event)" (totalRecords)="onTotalRecordsChange($event)" />

    <p-panel header="Results">
      <ng-template pTemplate="icons">
        <p-button
          [label]="(showFilters ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters ? 'Hide Filters' : 'Show Filters')"
          [icon]="showFilters ? 'pi pi-filter-slash' : 'pi pi-filter'"
          (click)="showFilters = !showFilters"
          variant="outlined"
          size="small"
        />
      </ng-template>
      <p-card data-variant="compact">
        <!-- No Results Message -->
        @if (!hasResults && isLoading === 0) {
          <p-message severity="secondary">{{
            ('No_Items_Inventory' | translateWF: 'No inventory found') ||
              'No inventory
          found'
          }}</p-message>
        }
        <!-- Lot Items Table -->
        @if (currentViewMode === 'lotItem' && lotItemResults.length > 0) {
          <p-table [value]="lotItemResults">
            <ng-template #header>
              <tr>
                <th>{{ 'Items_Inventory_Lot_Item_ID' | translateWF: 'Lot Item Number' }}</th>
                <th>{{ 'Common_Print_Label' | translateWF: 'Print Label' }}</th>
                <th>{{ 'Items_Inventory_Location_Name' | translateWF: 'Location' }}</th>
                <th>{{ 'Items_Inventory_Item_Code' | translateWF: 'Item Code' }}</th>
                <th>{{ 'Items_Inventory_Item_Description' | translateWF: 'Item Description' }}</th>
                <th>{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</th>
                <th>{{ 'Manufacturer_Lot_Number' | translateWF: 'Mfg Lot #' }}</th>
                <th>{{ 'Manufacturer_Label_ID' | translateWF: 'Mfg Label #' }}</th>
                <th>{{ 'Items_Inventory_Provider' | translateWF: 'Provider' }}</th>
                <th>{{ 'Items_Inventory_Net_Weight' | translateWF: 'Current Qty' }}</th>
                <th>{{ 'Items_Inventory_Original_Quantity' | translateWF: 'Original Qty' }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template #body let-item>
              <tr class="cursor-pointer">
                <td>
                  <a [routerLink]="['/items-inventory-detail']" [queryParams]="{ id: item.number }">{{ item.number }}</a>
                </td>
                <td>
                  <p-button icon="pi pi-print" [rounded]="true" [outlined]="true" (onClick)="printLotItemLabel(item)"></p-button>
                </td>
                <!-- <td>{{ getLocationName(item) }}</td> -->
                <td>
                  @if (item.currentLocationPathNames?.length) {
                    @for (segment of item.currentLocationPathNames; track $index; let last = $last) {
                      @if (last) {
                        <span>{{ segment | localized }}</span>
                      } @else {
                        <small>{{ segment | localized }} &gt; </small>
                      }
                    }
                  } @else {
                    <small>{{ (item.currentLocationName | localized) || '-' }}</small>
                  }
                </td>
                <td>{{ item.itemCode }}</td>
                <td>{{ getItemDisplayName(item) }}</td>
                <td>{{ getManufacturerName(item) }}</td>
                <td>{{ item.manufacturerLotNumber }}</td>
                <td>{{ item.manufacturerLabelNumber }}</td>
                <td>{{ getProviderName(item) }}</td>
                <td>{{ item.currentQuantity | number: '1.2-2' }}</td>
                <td>{{ item.originalQuantity | number: '1.2-2' }}</td>
                <td class="!text-end">
                  <p-button icon="pi pi-pencil" variant="text" [routerLink]="['/items-inventory-detail']" [queryParams]="{ id: item.number }" />
                </td>
              </tr>
            </ng-template>
          </p-table>
        }

        <!-- Inventory Summary Table -->
        @if (currentViewMode === 'inventorySummary' && inventorySummaryResults.length > 0) {
          <p-table [value]="inventorySummaryResults">
            <ng-template #header>
              <tr>
                <th>{{ 'Items_Inventory_Location_Code' | translateWF: 'Location Code' }}</th>
                <th>{{ 'Items_Inventory_Location_Name' | translateWF: 'Location Name' }}</th>
                <th>{{ 'Items_Inventory_Item_Code' | translateWF: 'Item Code' }}</th>
                <th>{{ 'Common_Print_Label' | translateWF: 'Print Label' }}</th>
                <th>{{ 'Items_Inventory_Item_Description' | translateWF: 'Item Description' }}</th>
                <th>{{ 'Items_Inventory_On_Hand' | translateWF: 'On Hand' }}</th>
                <th>{{ 'Items_Inventory_Reserved' | translateWF: 'Reserved' }}</th>
                <th>{{ 'Items_Inventory_Available' | translateWF: 'Available' }}</th>
              </tr>
            </ng-template>
            <ng-template #body let-item let-rowIndex="rowIndex">
              <tr>
                <td>{{ item.locationCode }}</td>
                <td>
                  @if (item.locationPathNames?.length) {
                    @for (segment of item.locationPathNames; track $index; let last = $last) {
                      @if (last) {
                        <span>{{ segment | localized }}</span>
                      } @else {
                        <small>{{ segment | localized }} &gt; </small>
                      }
                    }
                  } @else {
                    {{ getSummaryLocationName(item) }}
                  }
                </td>
                <td>{{ item.itemCode }}</td>
                <td>
                  <p-button icon="pi pi-print" [rounded]="true" [outlined]="true" (onClick)="printConsumableLabel(item)" />
                </td>
                <td>{{ getItemDisplayName(item) }}</td>
                <td>{{ item.onHandQuantity | number: '1.2-2' }}</td>
                <td>
                  <div class="row-start-center gap-1">
                    <span>{{ item.reservedQuantity | number: '1.2-2' }}</span>
                    @if (hasReservedDetails(item)) {
                      <i
                        class="pi pi-info-circle text-blue-500 cursor-pointer text-sm"
                        (click)="reservedPopover.toggle($event)"
                        [pTooltip]="'Items_Inventory_View_Reserved_Details' | translateWF: 'View reserved details'"
                        tooltipPosition="top"
                      ></i>
                      <p-popover #reservedPopover appendTo="body">
                        <div class="reserved-details-popover">
                          <div class="font-semibold mb-2 text-sm">
                            {{ 'Items_Inventory_Reserved_By' | translateWF: 'Reserved By' }}
                          </div>
                          <table class="reserved-details-table">
                            <thead>
                              <tr>
                                <th class="text-left pr-2">{{ 'Items_Inventory_Owner' | translateWF: 'Owner' }}</th>
                                <th class="text-right pr-2">{{ 'Items_Inventory_Qty' | translateWF: 'Qty' }}</th>
                                <th class="text-right pr-2">{{ 'Items_Inventory_Date' | translateWF: 'Date' }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (detail of getReservedDetails(item); track detail.owner?.reference) {
                                <tr>
                                  <td class="pr-2">{{ getOwnerReference(detail) }}</td>
                                  <td class="text-right pr-2">{{ detail.reservedQuantity | number: '1.2-2' }}</td>
                                  <td class="text-right pr-2">{{ formatReservedDate(detail) }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </p-popover>
                    }
                  </div>
                </td>
                <td>{{ item.availableQuantity | number: '1.2-2' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }

        <!-- Pagination -->
        @if (hasResults) {
          <div class="row-between-center custom__table__pagination bg__white">
            <div class="row-start-center gap-1">
              <!-- First Page -->
              <p-button
                type="button"
                icon="pi pi-angle-double-left"
                [disabled]="isFirstPage"
                (onClick)="firstPage()"
                text
                [pTooltip]="'Items_Inventory_First_Page' | translateWF: 'First page'"
                tooltipPosition="top"
              />
              <!-- Previous Page -->
              <p-button
                type="button"
                icon="pi pi-chevron-left"
                [disabled]="isFirstPage"
                (onClick)="prevPage()"
                text
                [pTooltip]="'Items_Inventory_Previous_Page' | translateWF: 'Previous page'"
                tooltipPosition="top"
              />
              <!-- Page Input -->
              <div class="row-sm-start-center mx-2">
                <span class="text-sm">{{ 'Items_Inventory_Page' | translateWF: 'Page' }}</span>
                <p-inputNumber
                  [(ngModel)]="pageInputValue"
                  [min]="1"
                  [max]="totalPages"
                  [showButtons]="false"
                  inputStyleClass="w-16 text-center"
                  (onKeyDown)="onPageInputKeydown($event)"
                  (onBlur)="goToPage()"
                />
                <span class="text-sm">{{ 'Items_Inventory_Of' | translateWF: 'of' }} {{ totalPages }}</span>
              </div>
              <!-- Next Page -->
              <p-button
                type="button"
                icon="pi pi-chevron-right"
                [disabled]="isLastPage"
                (onClick)="nextPage()"
                text
                [pTooltip]="'Items_Inventory_Next_Page' | translateWF: 'Next page'"
                tooltipPosition="top"
              />
              <!-- Last Page -->
              <p-button
                type="button"
                icon="pi pi-angle-double-right"
                [disabled]="isLastPage"
                (onClick)="lastPage()"
                text
                [pTooltip]="'Items_Inventory_Last_Page' | translateWF: 'Last page'"
                tooltipPosition="top"
              />
              <!-- Refresh -->
              <p-button type="button" icon="pi pi-refresh" (onClick)="resetTable()" text [pTooltip]="'Items_Inventory_Reset' | translateWF: 'Reset'" tooltipPosition="top" />
            </div>
            <div class="row-start-center cs-paginator-rows-only">
              <span class="p-paginator-current">
                {{ 'Items_Inventory_Showing' | translateWF: 'Showing' }} {{ pageSize * page - pageSize + 1 }}
                {{ 'Items_Inventory_To' | translateWF: 'to' }}
                {{ pageSize * page > totalRecords ? totalRecords : pageSize * page }}
                {{ 'Items_Inventory_Of' | translateWF: 'of' }} {{ totalRecords }} {{ 'Items_Inventory_Items_Per_Page' | translateWF: 'Items per page' }}:
              </span>
              <p-paginator
                [rows]="pageSize"
                [totalRecords]="totalRecords"
                [showCurrentPageReport]="false"
                [showPageLinks]="false"
                [showJumpToPageDropdown]="false"
                [rowsPerPageOptions]="[25, 50, 100]"
                (onPageChange)="setSearchPageSize($event)"
              />
            </div>
          </div>
        }
      </p-card>
    </p-panel>
  </div>
</main>
